import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  Container,
  Box,
  Stack,
  Title,
  Text,
  Button,
  Alert,
  Image,
  Group,
  Loader,
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconMail } from "@tabler/icons-react";
import rNameLogo from "../../assets/Reppi.svg";

function EmailVerificationPage() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [tokenEmail, setTokenEmail] = useState("");
  const navigate = useNavigate();
  const { logout, user, getVerifiedJWT, login } = useAuth();
  const { token } = useParams();

  // Helper function to decode JWT and extract email
  const getEmailFromToken = token => {
    try {
      if (!token) return "";

      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.email || "";
    } catch (error) {
      return "";
    }
  };

  // Handle token verification when token is present in URL
  useEffect(() => {
    const verifyEmailToken = async () => {
      if (token) {
        // Extract email from token
        const email = getEmailFromToken(token);
        setTokenEmail(email);

        // Set user in AuthContext with token data (only if not already set)
        if (email && (!user || user.jwt !== token)) {
          login({
            email: email,
            jwt: token,
            accessToken: token,
            verified: false,
            first_name: "",
            last_name: "",
            rep_id: "",
            role: "EMAIL_VERIFY",
          });
        }

        setVerifying(true);
        setError("");
        setMessage("");

        const jwt = await getVerifiedJWT();

        if (!jwt) {
          setError("Authentication failed");
          return;
        }

        try {
          const response = await fetch(
            getApiEndpoint("/v1/user/verify-email"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${jwt}`,
              },
              body: JSON.stringify({ token }),
            }
          );

          if (response.ok) {
            setVerified(true);
            // Redirect to login after a short delay
            setTimeout(() => {
              handleLogout();
            }, 4000);
          } else {
            setError(
              "Email verification failed. The token may be expired or invalid."
            );
          }
        } catch (error) {
          setError("Network error. Please try again.");
        } finally {
          setVerifying(false);
        }
      }
    };

    verifyEmailToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, user]);

  const handleResendEmail = async () => {
    const emailToUse = user?.email || tokenEmail;

    if (!emailToUse) {
      setError("Email address not found. Please try logging in again.");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const jwt = await getVerifiedJWT();

      const response = await fetch(
        getApiEndpoint("/v1/user/send-verification-email"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ email: emailToUse }),
        }
      );

      if (response.ok) {
        setMessage(`Email has been sent to ${emailToUse}`);
      } else {
        setError(
          "Failed to resend verification email. Please try again later."
        );
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout(false);
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--color-bg-secondary)",
        padding: "20px",
      }}
    >
      <Container size={500}>
        <Box
          bg="white"
          style={{
            borderRadius: "8px",
            boxShadow: "var(--shadow-md)",
            padding: "60px 40px",
            textAlign: "center",
          }}
        >
          <Box
            mb={40}
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/home")}
          >
            <Image src={rNameLogo} alt="Reppi" h={60} w="auto" mx="auto" />
          </Box>

          {token ? (
            // Token verification mode
            <Stack gap="md">
              <Title
                order={2}
                size="2xl"
                fw={600}
                c="var(--color-text-primary)"
              >
                {verifying
                  ? "Verifying Email..."
                  : verified
                    ? "Email Verified!"
                    : "Email Verification"}
              </Title>

              {verifying && (
                <Stack gap="md" align="center">
                  <Loader color="blue" size="lg" />
                  <Text size="sm" c="var(--color-text-secondary)" lh={1.5}>
                    Please wait while we verify your email address
                    {tokenEmail && ` ${tokenEmail}`}...
                  </Text>
                </Stack>
              )}

              {verified && (
                <Alert
                  icon={<IconCheck size="1rem" />}
                  title="Success!"
                  color="green"
                  mb="lg"
                  ta="left"
                >
                  Your email{tokenEmail && ` (${tokenEmail})`} has been
                  successfully verified! You will be redirected to the login
                  page shortly.
                </Alert>
              )}

              {!verifying && !verified && error && (
                <Text
                  size="sm"
                  c="var(--color-text-secondary)"
                  lh={1.5}
                  mb="lg"
                >
                  There was an issue verifying your email
                  {tokenEmail && ` ${tokenEmail}`}. Please try again or contact
                  support.
                </Text>
              )}

              {message && (
                <Alert
                  icon={<IconCheck size="1rem" />}
                  color="green"
                  mb="lg"
                  ta="left"
                >
                  {message}
                </Alert>
              )}

              {error && (
                <Alert
                  icon={<IconAlertCircle size="1rem" />}
                  color="red"
                  mb="lg"
                  ta="left"
                >
                  {error}
                </Alert>
              )}

              <Stack gap="sm" mt="lg">
                {!verifying && !verified && error && (
                  <Button
                    size="md"
                    onClick={handleResendEmail}
                    loading={loading}
                    leftSection={<IconMail size="1rem" />}
                  >
                    {loading ? "Sending..." : "Resend Email"}
                  </Button>
                )}

                {!verifying && !verified && (
                  <Group justify="center">
                    <Button variant="subtle" size="sm" onClick={handleLogout}>
                      Go to Login
                    </Button>
                  </Group>
                )}
              </Stack>
            </Stack>
          ) : (
            // Regular verification page mode
            <Stack gap="md">
              <Title
                order={2}
                size="2xl"
                fw={600}
                c="var(--color-text-primary)"
              >
                Verify Your Email
              </Title>

              <Text size="sm" c="var(--color-text-secondary)" lh={1.5} mb="lg">
                An email has been sent to{" "}
                <Text component="span" fw={600}>
                  {user?.email}
                </Text>
                . Please follow the link to verify your account.
              </Text>

              {message && (
                <Alert
                  icon={<IconCheck size="1rem" />}
                  color="green"
                  mb="lg"
                  ta="left"
                >
                  {message}
                </Alert>
              )}

              {error && (
                <Alert
                  icon={<IconAlertCircle size="1rem" />}
                  color="red"
                  mb="lg"
                  ta="left"
                >
                  {error}
                </Alert>
              )}

              <Stack gap="sm" mt="lg">
                <Button
                  size="md"
                  onClick={handleResendEmail}
                  loading={loading}
                  leftSection={<IconMail size="1rem" />}
                >
                  {loading ? "Sending..." : "Resend Email"}
                </Button>

                <Group justify="center">
                  <Button variant="subtle" size="sm" onClick={handleLogout}>
                    Logout
                  </Button>
                </Group>
              </Stack>
            </Stack>
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default EmailVerificationPage;
